import { has, update } from 'lodash';

const $inject = ['travelService', 'cartService', 'dateTimeService', 'flightFilterHelperService', 'itinerariesService'];

const flightFilterUnPackageService = function flightFilterUnPackageService(
  travelService,
  cartService,
  dateTimeService,
  flightFilterHelperService,
  itinerariesService
) {
  const service = {
    process: process,
    sortProcess: sortProcess,
    updateOptions: updateOptions,

    countStops: countStops,
    countAirports: countAirports,
    countCarrier: countCarrier,
    countPolicy: countPolicy,
    countCabin: countCabin,
    countAircraft: countAircraft,
    countFlexibility: countFlexibility,
    countSAFScore: countSAFScore,
    countPrice: countPrice,
    countCarbonFootprint: countCarbonFootprint,
    countLayoverOptions: countLayoverOptions,
    countTime: countTime,
    filterSegmentOptions: filterSegmentOptions,
    filterFareOptions: filterFareOptions,
    filterLayover: filterLayover,
    filterPrice: filterPrice,
    filterFlightTime: filterFlightTime,
    filterCarbonFootprint: filterCarbonFootprint,
    filterDisplayFare: filterDisplayFare,
    filterCartItems: filterCartItems,
    filterCheapestFares: filterCheapestFares,
  };
  const vm = this;

  function process(filters, activeFltr, opt, bumpFiltersApplied, getFiltersApplied, shouldCleanOptions) {
    if (shouldCleanOptions) {
      reselectOptions(filters);
    }

    if (!filters.carrier.options.length) {
      updateOptions(filters, null, null, shouldCleanOptions);
    }

    resetVisibility();
    let results = travelService.results;
    Object.values(filters)
      .filter((flt) => flt.unPkgFilter)
      .forEach((flt) => {
        results = results.map((result) =>
          Object.assign({}, result, {
            searchResults: result.searchResults,
          })
        );
        flt.unPkgFilter(results, bumpFiltersApplied);
      });

    results.forEach((result) => {
      result.searchResults.forEach((sr) => {
        const fareClasses = Object.values(sr.FareClasses).filter((fc) => fc);
        fareClasses.forEach((fc) => {
          fc.$state.visible = !fc.DisplayFare || !fc.DisplayFare.$state.visible;
        });

        if (fareClasses.every((fc) => !fc.DisplayFare || !fc.DisplayFare.$state.visible)) {
          sr.$state.visible = false;
          sr.$state.filteredByFares = true;
        }
      });
    });

    updateOptions(filters, activeFltr, opt, shouldCleanOptions);
  }

  function sortProcess(data, segIdx) {
    if (!data.property) return;
    let results = travelService.results;
    const getPropertySum = (trip, property) => {
      switch (property) {
        case 'Amount':
          const minAmount = Math.min(
            ...Object.values(trip.FareClasses)
              .filter((fc) => fc !== null)
              .map((fc) => fc.Amount)
          );
          return minAmount;
        case 'DepartureDateTime':
        case 'ArrivalDateTime':
          return new Date(trip[property]);
        case 'Preferred':
          return trip.Preferred ? 0 : 1;
        case 'LayoverDuration':
          const layoverinMins = trip.Details.filter((leg) => leg.Layover)
            .map((leg) => leg.Layover)
            .map((time) => time.split(',').reduce((acc, val) => acc + parseInt(val), 0));
          return layoverinMins.reduce((acc, val) => acc + val, 0);
        default:
          return trip[property];
      }
    };

    const sortSegmentResults = (searchResults) => {
      searchResults.sort((a, b) => {
        const aSum = getPropertySum(a, data.property);
        const bSum = getPropertySum(b, data.property);
        return data.isReverse ? bSum - aSum : aSum - bSum;
      });
    };

    sortSegmentResults(results[segIdx].searchResults);
  }

  function updateOptions(filters, activeFltr, opt, shouldCleanOptions) {
    let results = travelService.results;

    createStopOptions(filters);
    createCabinOptions(filters);
    createPolicyOptions(filters);
    createFlexibility(filters);

    const segmentsQuantity = results.length;

    for (let i = 0; i < segmentsQuantity; i++) {
      const result = results[i];
      for (let j = 0; j < result.searchResults.length; j++) {
        const sr = result.searchResults[j];
        createCarrierOptions(filters, sr, i);
        createAirportsOptions(filters, sr, i);
        createAircraftOptions(filters, sr, i);
        createSAFScoreOptions(filters, sr, i);
      }
    }

    if (shouldCleanOptions) {
      cleanOptions(filters);
    }

    addAllPreferredCarriers(filters);

    filters = Object.values(filters);

    filters.forEach((flt) => {
      if (filterHasIncludeExcludeSelectionsUnset(flt)) {
        if (segmentsQuantity == 0) {
          flt.includeExcludeSelections = [];
        } else {
          flt.includeExcludeSelections = new Array(segmentsQuantity + 1).fill(true);
        }
      }
      if (flt.options) flt.options.forEach((opt) => (Array.isArray(opt) ? opt.forEach((o) => (o.count = 0)) : (opt.count = 0)));
    });

    filters.filter((f) => f.unPkgCount).forEach((f) => f.unPkgCount(results));
  }

  function resetVisibility() {
    travelService.results.forEach((result) => {
      result.searchResults.forEach((sr) => {
        sr.$state.visible = true;
        sr.$state.filteredByFares = false;
        sr.$state.filteredBy = [];
        Object.values(sr.FareClasses)
          .filter((fare) => fare)
          .forEach((fc) => {
            fc.$state.visible = true;
            fc.$state.filteredBy = [];
            if (fc.DisplayFare) fc.DisplayFare.$state.visible = false;
            fc.AdditionalFares.forEach((fare) => {
              fare.$state.visible = true;
              fare.$state.filteredBy = [];
            });
          });
      });
    });
  }

  function createCarrierOptions(filters, sr, i) {
    const options = filters.carrier.options;
    if (!options[i]) options[i] = [];

    // ALL tab index is set to be the length of the results.
    var allTabIndex = travelService.results.length;
    if (!options[allTabIndex]) options[allTabIndex] = [];

    sr.Details.forEach((leg) => {
      const key = sr.Carrier;
      const id = 'carrier-' + key;
      const option = options[i].find((opt) => opt.id === id);
      if (!option) {
        options[i].push({
          label: sr.CarrierName,
          value: key,
          count: 1,
          id: id,
          preferred: sr.Preferred,
        });
      }

      // ALL tab
      const allTabOption = options[allTabIndex].find((opt) => opt.id === id);
      if (!allTabOption) {
        options[allTabIndex].push({
          label: sr.CarrierName,
          value: key,
          count: 1,
          id: id,
          preferred: sr.Preferred,
        });
      }
    });
  }

  function createAircraftOptions(filters, sr, i) {
    const options = filters.aircraft.options;
    if (!options[i]) options[i] = [];

    // ALL tab index is set to be the length of the results.
    var allTabIndex = travelService.results.length;
    if (!options[allTabIndex]) options[allTabIndex] = [];

    if (!sr.AircraftName) return;
    const aircraftNames = sr.AircraftName.split('/');
    aircraftNames.forEach((name) => {
      const key = name.trim();
      const id = 'aircraft-' + key.replace(/[^a-zA-Z0-9]/g, '');

      const option = options[i].find((opt) => opt.id === id);
      if (!option) {
        options[i].push({
          label: key,
          value: key,
          count: 0,
          id: id,
        });
      }

      // ALL tab
      const allTabOption = options[allTabIndex].find((opt) => opt.id === id);
      if (!allTabOption) {
        options[allTabIndex].push({
          label: key,
          value: key,
          count: 0,
          id: id,
        });
      }
    });

    options[i].sort((a, b) => a.label.localeCompare(b.label));
    options[allTabIndex].sort((a, b) => a.label.localeCompare(b.label));
  }

  function createAirportsOptions(filters, sr, i) {
    const options = filters.airports.options;
    if (!options[i]) options[i] = [];
    sr.Legs.forEach((leg) => {
      const key = leg.Origin.Code;
      const id = 'airport-' + key;
      const option = options[i].find((opt) => opt.id === id);
      if (option) return;
      options[i].push({
        label: leg.Origin.City + ' - ' + key,
        value: key,
        count: 1,
        id: id,
      });
    });
    options[i].sort((a, b) => a.label.localeCompare(b.label));
  }

  function addAllPreferredCarriers(filters) {
    let options = filters.carrier.options;
    options.forEach((option, i) => {
      if (option.some((carrier) => carrier.value === 'all')) return;
      option.sort((a, b) => a.label.localeCompare(b.label));
      if (option.some((carrier) => carrier.preferred)) {
        let preferredCount = option.reduce((sum, carrier) => sum + (carrier.preferred ? carrier.count : 0), 0);
        option.unshift({
          label: 'All Preferred Airlines',
          value: 'all',
          id: `carrier-all-preferred-${i}`,
          preferred: true,
          count: preferredCount,
        });
      }
    });
  }

  function createStopOptions(filters) {
    const options = filters.stops.options;

    // ALL tab index is set to be the length of the segments.
    for (var i = 0; i < travelService.results.length + 1; i++) {
      if (options[i]) return;
      options[i] = [
        {
          label: 'Nonstop Only',
          value: 0,
          id: `stops-${i}-0`,
        },
        {
          label: '2 stops',
          value: 2,
          id: `stops-${i}-2`,
        },
        {
          label: '1 stop',
          value: 1,
          id: `stops-${i}-1`,
        },
        {
          label: '3 or more stops',
          value: 'all',
          id: `stops-${i}-all`,
        },
      ];
    }
  }

  function createPolicyOptions(filters) {
    const options = filters.policy.options;
    const flyAmericaActEnabled = itinerariesService.setFlyAmericaEnabled();

    const baseOptions = [
      {
        label: 'In policy',
        value: 'in',
        id: '',
      },
      {
        label: 'Out of policy',
        value: 'out',
        id: '',
      },
    ];

    if (flyAmericaActEnabled) {
      baseOptions.push({
        label: 'Fly America Policy',
        value: 'flyAmerica',
        id: '',
      });
    }

    // ALL tab index is set to be the length of the results.
    for (var i = 0; i < travelService.results.length + 1; i++) {
      if (options[i]) return;
      options[i] = baseOptions.map((option) => Object.assign({}, option, { id: `policy-${i}-${option.value}` }));
    }
  }

  function createPriceOptions(filters) {
    const filter = filters.priceRange;
    const options = filter.options;
    let min = Number.MAX_VALUE;
    let max = 0;

    travelService.results.forEach((result) => {
      result.searchResults.forEach((sr) => {
        Object.values(sr.FareClasses).forEach((fare) => {
          if (!fare) return;
          const fareAmount = Math.round(fare.Amount);
          if (fareAmount < min) min = fareAmount;
          if (fareAmount > max) max = fareAmount;
        });
      });
    });

    filter.rzslider.floor = min;
    filter.rzslider.ceil = max;

    travelService.results.forEach((result, i) => {
      if (options[i]) return;
      options[i] = {
        min: min,
        max: max,
        defaultMin: min,
        defaultMax: max,
      };
    });
  }

  function createCabinOptions(filters) {
    const options = filters.cabin.options;

    // ALL tab index is set to be the length of the results.
    var allTabIndex = travelService.results.length;
    if (!options[allTabIndex]) options[allTabIndex] = [];

    travelService.results.forEach((result, i) => {
      if (!options[i]) options[i] = [];

      result.classes.forEach((cls) => {
        const id = 'cabin-' + cls.replace(/\s/g, '');

        const option = options[i].find((opt) => opt.id === id);
        if (!option) {
          options[i].push({
            label: cls,
            value: cls,
            count: 0,
            id: id,
          });
        }

        const allTabOption = options[allTabIndex].find((opt) => opt.id === id);
        if (!allTabOption) {
          options[allTabIndex].push({
            label: cls,
            value: cls,
            count: 0,
            id: id,
          });
        }
      });

      options[i].sort((a, b) => a.label.localeCompare(b.label));
    });

    options[allTabIndex].sort((a, b) => a.label.localeCompare(b.label));
  }

  function createFlexibility(filters) {
    const options = filters.flexibility.options;

    // ALL tab index is set to be the length of the results.
    for (var i = 0; i < travelService.results.length + 1; i++) {
      if (options[i]) return;
      options[i] = [
        {
          label: 'Refundable',
          value: 'refundable',
          id: `flexibility-${i}-refundable`,
        },
        {
          label: 'Non-Refundable',
          value: 'nonRefundable',
          id: `flexibility-${i}-nonRefundable`,
        },
      ];
    }
  }

  function createSAFScoreOptions(filters, sr, i) {
    const options = filters.safScore.options;
    if (!options[i]) options[i] = [];

    // ALL tab index is set to be the length of the results.
    var allTabIndex = travelService.results.length;
    if (!options[allTabIndex]) options[allTabIndex] = [];

    sr.Details.forEach((leg) => {
      if (sr.SafScore){
      const key = sr.SafScore;
      const id = 'safScore-' + key;
      const option = options[i].find((opt) => opt.id === id);
      if (!option) {
        options[i].push({
          label: sr.SafScore,
          value: key,
          count: 1,
          id: id,
        });
      }

      // ALL tab
      const allTabOption = options[allTabIndex].find((opt) => opt.id === id);
      if (!allTabOption) {
        options[allTabIndex].push({
          label: sr.SafScore,
          value: key,
          count: 1,
          id: id,
        });
      }}
    });

    options[i].sort((a, b) => a.label.localeCompare(b.label));
    options[allTabIndex].sort((a, b) => a.label.localeCompare(b.label));
  }

  function countPrice(results) {
    const isOptionsDefined = this.options && this.options.length > 0;

    let defaultOptions = { min: 0, max: 0, defaultMin: 0, defaultMax: 0 };
    let options = [];
    let allAmounts = [];
    this.segmentVisibleCounts = [];
    results.forEach((result, i) => {
      allAmounts[i] = [];
      this.segmentVisibleCounts[i] = 0;
      const previousOptions = isOptionsDefined ? this.options[i] : defaultOptions;

      result.searchResults.forEach((sr) => {
        const fareClasses = Object.values(sr.FareClasses).filter((fc) => fc);

        fareClasses.forEach((fc) => {
          const visibleFares = fc.AdditionalFares.filter((f) => travelService.isVisibleOrOnlyFilteredBy(sr.$state, f.$state, this.id));
          allAmounts[i] = [...allAmounts[i], ...visibleFares.map((m) => m.Amount)];
          this.segmentVisibleCounts[i] += visibleFares.filter((fare) => fare.$state.visible).length;
        });
      });

      options[i] = flightFilterHelperService.setPriceOptionForResult(allAmounts[i], previousOptions);
    });

    this.segmentCounts = allAmounts.map((a) => a.length);

    this.options = options;

    flightFilterHelperService.setSliderWithUpdatedOptions(this);
  }

  function countCarbonFootprint(results) {
    let defaultOptions = () => {
      return { min: 0, max: 0, defaultMin: 0, defaultMax: 0 };
    };
    const isOptionsDefined = this.options && this.options.length > 0;
    let options = [];
    let allCarbonEmissions = [];
    this.segmentVisibleCounts = [];
    results.forEach((result, i) => {
      allCarbonEmissions[i] = [];
      this.segmentVisibleCounts[i] = 0;
      const previousOptions = isOptionsDefined ? this.options[i] : defaultOptions();
      result.searchResults.forEach((sr) => {
        const fareClasses = Object.values(sr.FareClasses).filter((fc) => fc);
        fareClasses.forEach((fc) => {
          const visibleFares = fc.AdditionalFares.filter((f) => travelService.isVisibleOrOnlyFilteredBy(sr.$state, f.$state, this.id));
          visibleFares.forEach((f) => {
            allCarbonEmissions[i] = [...allCarbonEmissions[i], sr.MinCarbonEmission];
          });
          this.segmentVisibleCounts[i] += visibleFares.filter((fare) => fare.$state.visible).length;
        });
      });
      options[i] = flightFilterHelperService.setPriceOptionForResult(allCarbonEmissions[i], previousOptions);
    });
    this.segmentCounts = allCarbonEmissions.map((a) => a.length);
    this.options = options;
    flightFilterHelperService.setSliderWithUpdatedOptions(this);
  }

  function countLayoverOptions(results) {
    let defaultOptions = () => {
      return { min: 0, max: 0, defaultMin: 0, defaultMax: 0 };
    };
    const isOptionsDefined = this.options && this.options.length > 0;
    let options = [];
    let allLayovers = [];
    this.segmentVisibleCounts = [];
    results.forEach((result, i) => {
      allLayovers[i] = [];
      this.segmentVisibleCounts[i] = 0;
      const previousOptions = isOptionsDefined ? this.options[i] : defaultOptions();
      const visibleResults = result.searchResults.filter((sr) =>
        travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i)
      );
      allLayovers[i] = visibleResults.map((sr) => sr.TotalLayoverInMinutes);

      this.segmentVisibleCounts[i] += visibleResults.filter((sr) => sr.$state.visible).length;

      options[i] = flightFilterHelperService.setPriceOptionForResult(allLayovers[i], previousOptions);
    });

    this.segmentCounts = allLayovers.map((a) => a.length);
    this.options = options;
    flightFilterHelperService.setSliderWithUpdatedOptions(this);
  }

  function countTime(results) {
    let defaultOptions = () => {
      return {
        defaultMinDepart: 0,
        defaultMaxDepart: 0,
        maxDepart: 0,
        minDepart: 0,
        maxArrive: 0,
        minArrive: 0,
        defaultMinArrive: 0,
        defaultMaxArrive: 0,
      };
    };
    const isOptionsDefined = this.options && this.options.length > 0;
    let options = [];
    let allDepartureTimes = [];
    let allArrivalTimes = [];
    results.forEach((result, i) => {
      allDepartureTimes[i] = [];
      allArrivalTimes[i] = [];

      const previousOptions = isOptionsDefined ? this.options[i] : defaultOptions();
      allDepartureTimes[i] = result.searchResults
        .filter((sr) => travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i))
        .map((sr) => dateTimeService.getMinutesFromTime(sr.DepartureTime));
      allArrivalTimes[i] = result.searchResults
        .filter((sr) => travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i))
        .map((sr) => dateTimeService.getMinutesFromTime(sr.ArrivalTime));

      this.segmentCounts[i] = [];
      this.segmentCounts[i][0] = flightFilterHelperService.getTimeCounts(allDepartureTimes[i]);
      this.segmentCounts[i][1] = flightFilterHelperService.getTimeCounts(allArrivalTimes[i]);
      this.options[i] = flightFilterHelperService.setPriceOptionForResultTime(allDepartureTimes[i], allArrivalTimes[i], previousOptions);
    });

    flightFilterHelperService.setSliderWithUpdatedOptionsTime(this);
  }

  function countStops(results) {
    const counts = results.map((result, i) => {
      const count = {
        0: 0,
        1: 0,
        2: 0,
        all: 0,
      };

      for (const sr of result.searchResults.filter((sr) => travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i))) {
        const stops = sr.Stops;
        count[stops]++;
      }
      return count;
    });

    results.forEach((result, i) => {
      this.options[i].forEach((opt) => {
        opt.count = counts[i][opt.value];
      });

      // Adding all results together for the ALL tab.
      this.options[this.options.length - 1].forEach((opt) => {
        opt.count += counts[i][opt.value];
      });
    });
  }

  function countAirports(results) {
    results.forEach((result, i) => {
      result.searchResults
        .filter((sr) => travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i))
        .forEach((sr) => {
          sr.Legs.forEach((leg) => {
            this.options[i].forEach((opt) => {
              if (leg.Origin.Code === opt.value) opt.count++;
            });
          });
        });
    });
  }

  function countCarrier(results) {
    if (!this.options.length) return;
    const options = this.options.map((opts) => opts.filter((o) => o.value !== 'all'));

    results.forEach((result, i) => {
      if (!options[i]) return;

      options[i].forEach((o) => {
        o.count = result.searchResults
          .filter((sr) => travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i))
          .reduce((count, sr) => count + (sr.Carrier === o.value), 0);
      });

      // Adding all results together for the ALL tab.
      var allTabOptions = options[this.options.length - 1];
      allTabOptions.forEach((o) => {
        o.count += result.searchResults
          .filter((sr) => travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i))
          .reduce((count, sr) => count + (sr.Carrier === o.value), 0);
      });
    });

    // Setting all preferred count
    this.options.forEach((optionSet) => {
      const sumPreferredCounts = optionSet.reduce((sum, option) => (option.preferred ? sum + option.count : sum), 0);
      const allOption = optionSet.find((option) => option.value === 'all');
      if (allOption) allOption.count = sumPreferredCounts;
    });
  }

  function countPolicy(results) {
    let options = this.options;
    results.forEach((result, i) => {
      const counts = {
        in: 0,
        out: 0,
        flyAmerica: 0,
      };
      result.searchResults.forEach((sr) => {
        const fareClasses = Object.values(sr.FareClasses).filter((fc) => fc);
        let [hasInPolicy, hasOutPolicy, hasFlyAmerica] = [false, false, false];
        fareClasses.forEach((fc) => {
          const visibleFares = fc.AdditionalFares.filter((f) => travelService.isVisibleOrOnlyFilteredBy(sr.$state, f.$state, this.id));
          if (visibleFares.some((f) => f.PolicyExceptionDetails.InPolicy)) hasInPolicy = true;
          if (visibleFares.some((f) => !f.PolicyExceptionDetails.InPolicy)) hasOutPolicy = true;
          if (visibleFares.some((f) => f.PolicyExceptionDetails.FlyAmericaActInPolicy)) hasFlyAmerica = true;
        });
        if (hasInPolicy) counts.in++;
        if (hasOutPolicy) counts.out++;
        if (hasFlyAmerica) counts.flyAmerica++;
      });
      options[i].forEach((o) => (o.count = counts[o.value]));

      // Adding all results together for the ALL tab.
      options[this.options.length - 1].forEach((o) => (o.count += counts[o.value]));
    });
  }

  function countCabin(results) {
    results.forEach((result, i) => {
      this.options[i].forEach((o) => {
        o.count = result.searchResults.reduce(
          (count, sr) =>
            count +
            (sr.FareClasses[o.label] &&
            travelService.isVisibleOrOnlyFilteredBy(sr.$state, sr.FareClasses[o.label].DisplayFare.$state, this.id)
              ? 1
              : 0),
          0
        );
      });

      // Adding all results together for the ALL tab.
      this.options[this.options.length - 1].forEach((o) => {
        o.count += result.searchResults.reduce(
          (count, sr) =>
            count +
            (sr.FareClasses[o.label] &&
            travelService.isVisibleOrOnlyFilteredBy(sr.$state, sr.FareClasses[o.label].DisplayFare.$state, this.id)
              ? 1
              : 0),
          0
        );
      });
    });
  }

  function countAircraft(results) {
    results.forEach((result, i) => {
      result.searchResults
        .filter((sr) => travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i))
        .forEach((sr) => {
          if (!sr.AircraftName) return;
          const aircraftNames = sr.AircraftName.split('/');
          var uniqueAircraftNames = aircraftNames.filter(onlyUnique);
          uniqueAircraftNames.forEach((name) => {
            this.options[i].forEach((opt) => {
              if (name.trim() === opt.value) opt.count++;
            });

            // Adding all results together for the ALL tab.
            this.options[this.options.length - 1].forEach((opt) => {
              if (name.trim() === opt.value) opt.count++;
            });
          });
        });
    });
  }

  function countFlexibility(results) {
    let options = this.options;
    const counts = {
      refundable: 0,
      nonRefundable: 0,
    };
    results.forEach((result, i) => {
      result.searchResults.forEach((sr) => {
        const FareClasses = Object.values(sr.FareClasses).filter((fc) => fc);
        let [refundable, nonRefundable] = [false, false];
        FareClasses.forEach((fc) => {
          const visibleFares = fc.AdditionalFares.filter((f) => travelService.isVisibleOrOnlyFilteredBy(sr.$state, f.$state, this.id));
          if (visibleFares.some((fare) => fare && fare.Refundable)) refundable = true;
          if (visibleFares.some((fare) => fare && !fare.Refundable)) nonRefundable = true;
        });
        if (refundable) counts.refundable++;
        if (nonRefundable) counts.nonRefundable++;
      });

      options[i].forEach((o) => (o.count = counts[o.value]));

      // Adding all results together for the ALL tab.
      options[this.options.length - 1].forEach((o) => (o.count += counts[o.value]));
    });
  }

  function countSAFScore(results){
    results.forEach((result, i) => {
      result.searchResults
        .filter((sr) => travelService.isVisibleOrOnlyFilteredBySegment(sr.$state, null, this.id, i))
        .forEach((sr) => {
          if (!sr.SafScore) return;
          const safScore = sr.SafScore;
            this.options[i].forEach((opt) => {
              if (safScore === opt.value) opt.count++;
            });

            // Adding all results together for the ALL tab.
            this.options[this.options.length - 1].forEach((opt) => {
              if (safScore === opt.value) opt.count++;
            });
          });
        });
  }

  function filterSegmentOptions(results, bumpFiltersApplied) {
    const includeExcludeSelections = this.includeExcludeSelections;
    results.forEach((result, i) => {
      const isAnySelected = !!includeExcludeSelections && (includeExcludeSelections[i] === true || includeExcludeSelections[i] === false);
      const isSelected = !!isAnySelected ? includeExcludeSelections[i] : true;

      this.selected = this.selected || [];
      let values = this.selected[i] || [];
      if (!values.length) return;

      bumpFiltersApplied();
      result.searchResults.forEach((sr) => {
        let isFareVisible = false;
        switch (this.id) {
          case 'airport':
            isFareVisible = sr.Legs.some((leg) => {
              const includesOriginCode = values.includes(leg.Origin.Code);
              return isSelected ? includesOriginCode : !includesOriginCode;
            });
            break;
          case 'aircraft':
            const aircraftNames = sr.AircraftName.split('/');
            isFareVisible = aircraftNames.some((name) => {
              const includesAircraftName = values.includes(name.trim());
              return isSelected ? includesAircraftName : !includesAircraftName;
            });
            break;
          case 'carrier':
            values = values.filter((value) => value !== 'all');
            if (!values.length) return;
            isFareVisible = values.includes(sr.Carrier);
            isFareVisible = isSelected ? isFareVisible : !isFareVisible;
            break;
          case 'safScore':
            values = values.filter((value) => value !== 'all');
            if (!values.length) return;
            isFareVisible = values.includes(sr.SafScore);
            isFareVisible = isSelected ? isFareVisible : !isFareVisible;
            break;
          default:
            isFareVisible = values.includes(sr[this.reference]);
            isFareVisible = isSelected ? isFareVisible : !isFareVisible;
        }
        sr.$state.visible = sr.$state.visible ? isFareVisible : sr.$state.visible;
        if (!isFareVisible) {
          sr.$state.filteredBy.push(travelService.GetIdSeg(this.id, i));
        }
      });
    });
  }

  function filterFareOptions(results, bumpFiltersApplied) {
    this.selected = this.selected || [];
    const id = this.id;
    results.forEach((result, i) => {
      const values = this.selected[i] || [];
      if (!values.length) return;

      bumpFiltersApplied();
      result.searchResults.forEach((sr) => {
        const fareClasses = Object.entries(sr.FareClasses).filter(([key, fc]) => fc);

        fareClasses.forEach(([key, fare]) => {
          let visibleFare = null;
          fare.AdditionalFares.filter((fc) => fc).forEach((fc) => {
            let isFareVisible = false;
            switch (id) {
              case 'cabin':
                isFareVisible = values.includes(fc.Class.replace(/\s/g, ''));
                break;

              case 'policy':
                values.forEach((val) => {
                  switch (val) {
                    case 'in':
                    case 'out':
                      isFareVisible = values.includes(fc.PolicyExceptionDetails.InPolicy ? 'in' : 'out');
                      break;
                    case 'flyAmerica':
                      isFareVisible = fc.PolicyExceptionDetails.FlyAmericaActInPolicy;
                      break;
                  }
                });
                break;

              case 'flexibility':
                isFareVisible = values.includes(fc.Refundable ? 'refundable' : 'nonRefundable');
                break;
            }
            fc.$state.visible = fc.$state.visible ? isFareVisible : fc.$state.visible;
            if (!isFareVisible) {
              fc.$state.filteredBy.push(this.id);
            }
            if (fc.$state.visible && (!visibleFare || fc.Amount < visibleFare.Amount)) {
              fc.$state.DisplayFare = fare;
              visibleFare = fare;
            }
          });
        });
      });
    });
  }

  function filterLayover(results, bumpFiltersApplied) {
    results.forEach((result, i) => {
      const option = this.options[i];
      const { min, max, defaultMin, defaultMax } = option;
      const isActive = min > defaultMin || max < defaultMax;
      if (!isActive) return;
      bumpFiltersApplied(this, i);
      result.searchResults.forEach((sr) => {
        let isFareVisible = sr.TotalLayoverInMinutes >= min && sr.TotalLayoverInMinutes <= max;
        sr.$state.visible = sr.$state.visible ? isFareVisible : sr.$state.visible;
        if (!isFareVisible) {
          sr.$state.filteredBy.push(travelService.GetIdSeg(this.id, i));
        }
      });
    });
  }

  function filterCarbonFootprint(results, bumpFiltersApplied) {
    results.forEach((result, i) => {
      const option = this.options[i];
      const { min, max, defaultMin, defaultMax } = option;
      const isActive = min > defaultMin || max < defaultMax;
      if (!isActive) return;
      if (isActive && this.isResultsFilter) bumpFiltersApplied(this, i);

      result.searchResults.forEach((sr) => {
        const fareClasses = Object.entries(sr.FareClasses).filter(([key, fc]) => fc);
        fareClasses.forEach(([key, fc]) => {
          const carbon = fc.CarbonEmission;
          fc.AdditionalFares.forEach((fare) => {
            let isFareVisible = carbon >= min && carbon <= max;
            fare.$state.visible = fare.$state.visible ? isFareVisible : fare.$state.visible;
            if (!isFareVisible) {
              fare.$state.filteredBy.push(this.id);
            }
          });
          fc.$state.visible = fc.AdditionalFares.some((fare) => fare.$state.visible);
        });
      });
    });
  }

  function filterPrice(results, bumpFiltersApplied) {
    results.forEach((result, i) => {
      const option = this.options[i];
      const { min, max, defaultMin, defaultMax } = option;
      const isActive = min > defaultMin || max < defaultMax;
      if (!isActive) return;
      if (isActive && this.isResultsFilter) bumpFiltersApplied(this);
      result.searchResults.forEach((sr) => {
        Object.values(sr.FareClasses).forEach((fc) => {
          fc.AdditionalFares.forEach((fare) => {
            let isFareVisible = fare.Amount >= min && fare.Amount <= max;
            fare.$state.visible = fare.$state.visible ? isFareVisible : fare.$state.visible;
            if (!isFareVisible) {
              fare.$state.filteredBy.push(this.id);
            }
          });
          let isFareVisible = fc.AdditionalFares.some((fare) => fare.$state.visible) || (fc.Amount >= min && fc.Amount <= max);
          fc.$state.visible = fc.$state.visible ? isFareVisible : fc.$state.visible;
          if (!isFareVisible) {
            fc.$state.filteredBy.push(this.id);
          }
        });
      });
    });
  }

  function filterFlightTime(results, bumpFiltersApplied) {
    results.forEach((result, i) => {
      const option = this.options[i];
      const ceil = this.rzslider.ceil;
      const { minArrive, maxArrive, defaultMinArrive, defaultMaxArrive, minDepart, maxDepart, defaultMinDepart, defaultMaxDepart } = option;
      const isActive =
        minArrive > defaultMinArrive || maxArrive < defaultMaxArrive || minDepart > defaultMinDepart || maxDepart < defaultMaxDepart;
      if (!isActive) return;
      if (isActive && this.isResultsFilter) bumpFiltersApplied(this, i);
      result.searchResults.forEach((sr) => {
        const departTime = dateTimeService.getMinutesFromTime(sr.DepartureTime);
        const arriveTime = dateTimeService.getMinutesFromTime(sr.ArrivalTime);
        let isFareVisible = departTime >= minDepart && departTime <= maxDepart && arriveTime >= minArrive && arriveTime <= maxArrive;
        sr.$state.visible = sr.$state.visible ? isFareVisible : sr.$state.visible;
        if (!isFareVisible) {
          sr.$state.filteredBy.push(travelService.GetIdSeg(this.id, i));
        }
      });
    });
  }

  function filterDisplayFare(results) {
    results.forEach((result, i) => {
      result.searchResults
        .filter((sr) => sr.$state.visible)
        .forEach((sr) => {
          Object.values(sr.FareClasses).forEach((fareClass) => {
            Object.values(fareClass.AdditionalFares)
              .filter((fc) => fc.$state.visible)
              .forEach((fare, fareIndex) => {
                if (fare.$state.selected) {
                  let selectedBundle = fare.Bundles.find((bundle) => bundle.selected === true);
                  if (!!selectedBundle) {
                    let bundledFare = Object.assign({}, fare);
                    bundledFare.Amount += selectedBundle.price;
                    fareClass.DisplayFare = bundledFare;
                  } else {
                    fareClass.DisplayFare = fare;
                  }
                  return;
                } else {
                  if (fareIndex === 0) fareClass.DisplayFare = fare;
                }
              });
          });
        });
    });
  }

  function filterCartItems(results) {
    let hasMatchingFares = true;
    const cartItems = cartService.items;
    if (!cartItems.length) {
      results.forEach((result, i) => {
        result.searchResults.forEach((sr) => {
          Object.values(sr.FareClasses).forEach((fc) => {
            fc.AdditionalFares.forEach((fare) => {
              fare.$state.matchedOffset = 0;
              fare.$state.displayMatchedOffset = false;
            });
          });
        });
      });
      return;
    }
    results.forEach((result, i) => {
      let resultsCartItem = cartItems.find((cartItem) => cartItem.orderBy === i);
      result.searchResults.forEach((sr) => {
        if (resultsCartItem && resultsCartItem.result === sr) {
          resultsCartItem.fare.$state.selected = true;
          sr.$state.selected = true;
          Object.values(sr.FareClasses).forEach((fareClass) => {
            fareClass.AdditionalFares.forEach((fare) => {
              if (resultsCartItem.fare.Identifier !== fare.Identifier) {
                fare.$state.visible = false;
              }
            });
          });
        }

        if (Number.isInteger(result.matchingResultsIndex)) {
          const matchingResultsCartItem = cartItems.find((cartItem) => {
            return (
              cartItem.matchingIndex !== result.matchingResultsIndex || result.matchingResultsIndexes.indexOf(cartItem.matchingIndex) !== -1
            );
          });

          if (matchingResultsCartItem) {
            var additionalFares = matchingResultsCartItem.result.FareClasses[matchingResultsCartItem.fare.Class].AdditionalFares;
            if (!resultsCartItem || cartItems.indexOf(matchingResultsCartItem) < cartItems.indexOf(resultsCartItem)) {
              hasMatchingFares = false;

              Object.values(sr.FareClasses).forEach((fareClass) => {
                fareClass.AdditionalFares.forEach((fare) => {
                  let matchingFare = fare.MatchedFares.find((matchedFare) => {
                    return matchedFare.Identifier === matchingResultsCartItem.fare.Identifier;
                  });

                  fare.$state.displayMatchedOffset = true;

                  if (matchingFare) {
                    fare.$state.matchedOffset = matchingFare.OffsetAmount;
                    hasMatchingFares = true;
                    return;
                  }

                  let findSubFare = null;

                  fare.MatchedFares.forEach((matchedFare) => {
                    if (matchedFare.Identifier === matchingResultsCartItem.fare.Identifier) return true;

                    var item = additionalFares.find((subFare) => matchedFare.Identifier === subFare.Identifier);
                    if (item) {
                      findSubFare = item;
                      matchingFare = matchedFare;
                    }
                  });
                  if (matchingFare) {
                    fare.$state.matchedOffset = findSubFare.Amount + matchingFare.OffsetAmount - matchingResultsCartItem.fare.Amount;
                    hasMatchingFares = true;
                    return;
                  }
                  fare.$state.visible = false;
                  fare.$state.matchedOffset = -1;
                });
              });
            }
          }
        }
      });
    });
  }

  function filterCheapestFares(segIdx) {
    let results = travelService.results;
    let cheapestFare = results[segIdx];
    cheapestFare.searchResults.forEach((sr) => {
      Object.entries(sr.FareClasses)
        .filter(([fcName, fc]) => fc)
        .forEach(([fcName, fc]) => {
          fc.DisplayFare.$state.visible = false;
          fc.$state.visible = false;
          fc.AdditionalFares.forEach((fare) => (fare.$state.visible = false));
        });
    });

    cheapestFare.searchResults.forEach((sr) => {
      Object.entries(sr.FareClasses)
        .filter(([fcName, fc]) => fc)
        .forEach(([fcName, fc]) => {
          fc.AdditionalFares.forEach((fare) => {
            if (fare.Cheapest) {
              fare.$state.visible = true;
              fc.DisplayFare = fare;
            }
          });
        });
    });
    cheapestFare.searchResults.forEach((sr) => {
      const fareClasses = Object.values(sr.FareClasses).filter((fc) => fc);
      if (fareClasses.every((fc) => !fc.DisplayFare || !fc.DisplayFare.$state.visible)) sr.$state.visible = false;
    });
  }

  function filterHasIncludeExcludeSelectionsUnset(filter) {
    return !!filter.includeExcludeSelections && filter.includeExcludeSelections.length === 0;
  }

  function onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  function cleanSelectedList(filters, filter) {
    var selectedList = filters[filter].selected;
    for (var i = 0; i < filters[filter].options.length; i++) {
      if (selectedList[i]) {
        selectedList[i] = selectedList[i].filter(onlyUnique);
      } else {
        selectedList[i] = [];
      }
    }
  }

  function reselectOptions(filters) {
    var filtersToCheck = ['stops', 'carrier', 'cabin', 'policy', 'aircraft', 'flexibility'];
    var allTabIndex = travelService.results.length;

    filtersToCheck.forEach((filter) => {
      cleanSelectedList(filters, filter);
      var selectedList = filters[filter].selected;
      var options = filters[filter].options;
      if (options[options.length - 1]) {
        // Checking all segment options (except the last one since that one is for the ALL tab).
        for (var i = 0; i < options.length - 1; i++) {
          // Checking if the current segment is in the cart.
          if (travelService.results[i] && travelService.results[i].selected) {
            options[i].forEach((option) => {
              // Making sure that the segment's options match the isActive of the ALL tab options.
              var matchingAllTabOption = options[allTabIndex].find((opt) => opt.label === option.label);
              if (matchingAllTabOption && matchingAllTabOption.isActive) {
                option.isActive = matchingAllTabOption.isActive;
                var selected = selectedList[i];

                var hasValue = selected.find((opt) => opt.value === option.value);

                if (option.isActive) {
                  if (!hasValue) {
                    selected.push(option.value);
                  }
                } else {
                  selected.splice(selected.indexOf(option.value), 1);
                }
              }
            });
          }
        }
      }
    });
  }

  function cleanOptions(filters) {
    var filtersToCheck = ['stops', 'carrier', 'cabin', 'policy', 'aircraft', 'flexibility','safScore'];

    filtersToCheck.forEach((filter) => {
      cleanSelectedList(filters, filter);

      var options = filters[filter].options;
      if (options[options.length - 1]) {
        // Checking all segment options (except the last one since that one is for the ALL tab).
        var optionsToAddToAllTab = {};
        for (var i = 0; i < options.length - 1; i++) {
          options[i].forEach((option) => {
            if (!option.originalCount) {
              option.originalCount = option.count;
            }

            if (option.originalCount >= 0) {
              if (optionsToAddToAllTab[option.label] && option.originalCount > 0) {
                optionsToAddToAllTab[option.label]++;
              } else {
                optionsToAddToAllTab[option.label] = 1;
              }
            }
          });
        }

        var optionsToRemove = [];
        for (const [label, count] of Object.entries(optionsToAddToAllTab)) {
          if (count != options.length - 1) {
            optionsToRemove.push(label);
          }
        }

        var newFilterList = [];
        options[options.length - 1].forEach((option) => {
          var hasOption = optionsToRemove.find((opt) => opt == option.label);
          if (!hasOption) {
            newFilterList.push(option);
          }
        });

        options[options.length - 1] = newFilterList;
      }
    });
  }

  filterSegmentOptions = flightFilterHelperService.memoize(filterSegmentOptions);
  filterFareOptions = flightFilterHelperService.memoize(filterFareOptions);

  return service;
};

flightFilterUnPackageService.$inject = $inject;

export default flightFilterUnPackageService;



// WEBPACK FOOTER //
// ./src/services/flightFilterUnPackageService.js